import React from "react";
import { observer } from "mobx-react-lite";
import { searchStore } from "features/ProductSearch/SearchStore";
import { MEStore } from "features/MappingEditor/MappingEditorStore";
import { SingleItem } from "api-client";
import MEObjectDetail from "./features/ObjectDetailsSlideOver";
import PanelLayout from "./layout/PanelLayout";
import useMappingEditor from "./useMappingEditor";
import ConfirmModal from "components/ConfirmModal";
import { useTranslation } from "react-i18next";
import { rulesSetStore } from "features/RulesSet/RulesSetStore";
import { buildingStore } from "store/Building/BuildingStore";
import ProductDetailsModal from "features/ProductDetailsModal";
import { BPEStore } from "features/BuildingProductEditor/ProductEditorStore";
import EPDSearchModal from "features/EPDSearchModal";
import useFeatureFlag from "hooks/useFeatureFlag";
import ProductSearchModal from "features/AdvancedProductSearch/ProductSearchModal";
import ProductSearch from "features/ProductSearch";
import LoadingIcon from "components/LoadingIcon";
import { dynamicTableStore } from "features/DynamicTable/DynamicTableStore";
import { mappingSnapshotStore } from "features/MappingEditor/features/MappingSnapshotUndo/MappingSnapshotStore";

const DynamicMappingEditor = observer(() => {
  const { mappingToFile, mappingToObjects } = useMappingEditor();
  const { t } = useTranslation();
  const { isFeatureVisible } = useFeatureFlag();

  React.useEffect(() => {
    searchStore.setUsingOrganisationSearch(false);
    BPEStore.fetchCPXValues();
    MEStore.getFileCalculationsStatus();

    const timeoutRef = { current: 0 };

    const scheduleNext = () => {
      timeoutRef.current = window.setTimeout(
        () => {
          MEStore.getFileCalculationsStatus();
          scheduleNext();
        },
        MEStore.fileCalculationsStatus ? 5000 : 10000
      );
    };

    scheduleNext();

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, []);

  React.useEffect(() => {
    rulesSetStore.fetchRulesList(
      buildingStore.currentBuilding?.organisation_id
    );
  }, [buildingStore.currentBuilding]);

  const prevFileCalculationsStatus = React.useRef(false);

  React.useEffect(() => {
    const wasCalculating = prevFileCalculationsStatus.current;
    const isCalculating = MEStore.fileCalculationsStatus;

    if (!wasCalculating && isCalculating) {
      MEStore.setIsUndoInProgress(true);
    } else if (wasCalculating && !isCalculating) {
      dynamicTableStore.fetchObjectsList(0, true);
      if (MEStore.currentFileID) {
        mappingSnapshotStore.listSnapshots(MEStore.currentFileID, true);
        mappingSnapshotStore.listMappingSnapshots(MEStore.currentFileID);
        MEStore.getObjectsMappingStatus(MEStore.currentFileID);
      }
      MEStore.setIsUndoInProgress(false);
    }

    prevFileCalculationsStatus.current = isCalculating;
  }, [MEStore.fileCalculationsStatus]);

  async function matchProductAndUpdate(
    product_id: string,
    current_object: SingleItem
  ) {
    if (MEStore.selectAllChecked && !current_object) {
      await mappingToFile("manual_match", product_id);
    } else {
      await mappingToObjects(
        "manual_match",
        product_id,
        MEStore.matchForSelectedItems ? undefined : current_object
      );
    }
  }

  const onCloseObjectDetailView = () => {
    MEStore.setOpenedObjectDetails(false);
    MEStore.setCurrentRow(undefined);
  };

  const onCloseProductDetailView = () => {
    MEStore.setOpenProductDetailsPopup({ open: false });
  };

  function loadingView() {
    return (
      <div className="absolute flex flex-col items-center justify-center bg-gray-50 !flex-grow z-4 w-full min-h-full">
        <LoadingIcon className="min-h-20 min-w-20 my-4" />
        <div className="mb-4 text-gray-500">
          {t("mappingEditor.objectAreRecalculated")}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-grow relative">
      {MEStore.isUndoInProgress && loadingView()}

      <PanelLayout />
      {searchStore.openProductSearch.state &&
        !isFeatureVisible("new_product_search") && (
          <ProductSearch
            open={searchStore.openProductSearch.state}
            close={() => searchStore.setOpenProductSearch({ state: false })}
            currentRow={MEStore.currentRow?.original as SingleItem}
            matchProductAndUpdate={matchProductAndUpdate}
          />
        )}

      {searchStore.openProductSearch.state &&
        isFeatureVisible("new_product_search") && (
          <ProductSearchModal
            open={searchStore.openProductSearch.state}
            close={() => searchStore.setOpenProductSearch({ state: false })}
            currentRow={MEStore.currentRow?.original as SingleItem}
            matchProductAndUpdate={matchProductAndUpdate}
          />
        )}

      {searchStore.openEPDSearch && (
        <EPDSearchModal
          open={searchStore.openEPDSearch.state}
          close={() => searchStore.setOpenEPDSearch({ state: false, term: "" })}
          searchTerm={searchStore.openEPDSearch.term}
          generate
        />
      )}
      <MEObjectDetail
        open={MEStore.openedObjectDetails}
        close={onCloseObjectDetailView}
        object={MEStore.getCurrentRowObject()}
      />
      <ProductDetailsModal
        productId={MEStore.openProductDetailsPopup.id}
        productName={MEStore.openProductDetailsPopup.name}
        productType={MEStore.openProductDetailsPopup.type}
        open={MEStore.openProductDetailsPopup.open}
        close={onCloseProductDetailView}
      />

      <ConfirmModal
        open={MEStore.isConfirmationVisible}
        close={() => MEStore.cancelMappingToFile()}
        title={t("mappingEditor.fileMappingConfirmTitle")}
        descriptionMesage={t("mappingEditor.fileMappingConfirmDescription")}
        onConfirm={() => MEStore.confirmMappingToFile()}
        cancelTitle={t("commons.cancel")}
        confirmButtonTitle={t("mappingEditor.changeMappings")}
        containerClassName="!min-w-[640px]"
      />
    </div>
  );
});

export default React.memo(DynamicMappingEditor);
